import { useDispatch, useSelector } from 'react-redux';
import { VehicleType } from '../../enums/TurnVehicleType';
import { AppState } from '../../store/AppState';
import { setVehicleTypes } from '../../store/inventory/InventoryFilterActions';
import { InventoryFilterState } from '../../store/inventory/InventoryFilterReducer';
import { applyFilters, useFilteredInventoryData } from '../UseFilteredInventoryData';
import { ComparatorFn, LabelType, UseDropdownLabel } from './UseDropdownLabel';

export const useVehicleTypes = (): UseVehicleTypesReturnType => {
  const { unfilteredData } = useFilteredInventoryData();
  const filters = useSelector<AppState, InventoryFilterState>((state) => state.inventoryFilter);
  const dispatch = useDispatch();
  const isDisabled = false;

  const handleChange = (item: string) => {
    if (filters.vehicleTypes.includes(item as VehicleType)) {
      dispatch(setVehicleTypes(filters.vehicleTypes.filter((type) => type !== item)));
    } else {
      dispatch(setVehicleTypes([...filters.vehicleTypes, item] as VehicleType[]));
    }
  };

  const countByVehicleType = (vhTyp: string) => {
    let count = 0;

    if (vhTyp === VehicleType.New) {
      count = applyFilters(
        unfilteredData.filter((item) => item.vehicleType === 'New'),
        { ...filters, vehicleTypes: [...filters.vehicleTypes, vhTyp] },
      ).length;
    }

    if (vhTyp === VehicleType.PreOwned) {
      count = applyFilters(
        unfilteredData.filter((item) => item.vehicleType === 'Pre-owned'),
        { ...filters, vehicleTypes: [...filters.vehicleTypes, vhTyp] },
      ).length;
    }

    if (vhTyp === VehicleType.CertifiedPreOwned) {
      count = applyFilters(
        unfilteredData.filter((item) => item.vehicleType === 'Certified Pre-owned'),
        { ...filters, vehicleTypes: [...filters.vehicleTypes, vhTyp] },
      ).length;
    }

    if (vhTyp === VehicleType.Sample) {
      count = applyFilters(
        unfilteredData.filter((item) => item.vehicleType === null),
        { ...filters, vehicleTypes: [...filters.vehicleTypes, vhTyp] },
      ).length;
    }

    return count;
  };

  const sortComparator: ComparatorFn<string> = (a: string, b: string) => b.localeCompare(a);

  const label = () => {
    let result;
    if (filters.vehicleTypes.length === 0) {
      result = UseDropdownLabel(
        Object.keys(VehicleType),
        Object.keys(VehicleType),
        LabelType.Numbers,
        sortComparator,
      );
    }
    result = UseDropdownLabel(
      filters.vehicleTypes,
      Object.keys(VehicleType),
      LabelType.Numbers,
      sortComparator,
    );
    if (result === 'All') {
      return '';
    }
    return result;
  };

  const toggleSelection = () => {
    filters.vehicleTypes.length > 0
      ? dispatch(setVehicleTypes([]))
      : dispatch(
          setVehicleTypes([
            VehicleType.New,
            VehicleType.PreOwned,
            VehicleType.CertifiedPreOwned,
            VehicleType.Sample,
          ]),
        );
  };

  return {
    pickedVehicleTypes: filters.vehicleTypes,
    handleChange,
    countByVehicleType,
    isDisabled,
    label: label(),
    toggleSelection,
    allVehicleTypes: [
      VehicleType.New,
      VehicleType.PreOwned,
      VehicleType.CertifiedPreOwned,
      VehicleType.Sample,
    ],
  };
};

export type UseVehicleTypesReturnType = {
  pickedVehicleTypes: readonly VehicleType[];
  countByVehicleType: (vehicleType: VehicleType) => number;
  isDisabled: boolean;
  label: string;
  toggleSelection: () => void;
  allVehicleTypes: readonly VehicleType[];
  handleChange: (vehcleType: VehicleType) => void;
};
